import { useMemo, useState, useCallback } from "react"

// ** Third Party Components
import { useQuery } from "@tanstack/react-query"
import { BarChart3, BookOpen, Banknote, FolderOpen } from "lucide-react"

// ** UI Components
import { Tabs, TabsList, TabsTrigger } from "@components/common/tabs"

// ** API Routes
import { PROJECTS_BY_CURRENT_USER_FILTER } from "@pma/utils/api-routes"

// ** Types
import { AnalyticsFilters } from "@pma/types"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Utils
import axiosServices from "@pma/utils/axios"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import { FileAnalytics } from "./files"
import { TasksAnalytics } from "./tasks"
import { WorkDiaryAnalytics } from "./work-diary"
import { FinancialAnalytics } from "./financial"
import { AnalyticsFilterBar, EMPTY_FILTERS } from "./analytics-filters"

// ---------------------------------------------------------------------------
// Tab badge — renders only when count > 0
// ---------------------------------------------------------------------------
function TabBadge({ count }: { count: number | null }) {
  if (count === null || count === 0) return null
  return (
    <span className='ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary'>
      {count}
    </span>
  )
}

export function AnalyticsDashboard() {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()

  const orgId = authConfig?.last_organization_id
  const isHebrew = locale === "he"

  const [activeTab, setActiveTab] = useState("tasks")
  const [filters, setFilters] = useState<AnalyticsFilters>(EMPTY_FILTERS)
  const [tabCounts, setTabCounts] = useState<Record<string, number | null>>({
    tasks: null,
    diaries: null,
    financial: null,
    files: null,
  })

  const handleGetProjectList = useCallback(async () => {
    const response = await axiosServices.post(PROJECTS_BY_CURRENT_USER_FILTER, {
      paging_start_page: 1,
      paging_per_page: 100,
      by_organization_id: orgId,
    })

    return response.data
  }, [orgId])

  const { data } = useQuery({
    queryKey: ["shared-analytics-projects", orgId],
    queryFn: handleGetProjectList,
  })

  const projectOptions = useMemo(() => {
    const options: any[] = []
    if (data) {
      data?.items?.map((project: any) => {
        options.push({ id: project.id, name: project.title })
      })
    }
    return options
  }, [data])

  const handleTasksCount = useCallback((n: number) => setTabCounts((p) => ({ ...p, tasks: n })), [])
  const handleDiariesCount = useCallback(
    (n: number) => setTabCounts((p) => ({ ...p, diaries: n })),
    [],
  )
  const handleFinancialCount = useCallback(
    (n: number) => setTabCounts((p) => ({ ...p, financial: n })),
    [],
  )
  const handleFilesCount = useCallback((n: number) => setTabCounts((p) => ({ ...p, files: n })), [])

  return (
    <div dir={isHebrew ? "rtl" : "ltr"} className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-xl font-bold leading-tight'>{translate("Analytics Dashboard")}</h1>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            {translate("Overview of all projects")}
          </p>
        </div>
      </div>

      <AnalyticsFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        projectOptions={projectOptions}
      />
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className='w-full'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        <TabsList className='grid w-full grid-cols-4'>
          <TabsTrigger value='tasks' className='gap-2'>
            <BarChart3 className='h-5 w-5' />
            <span className='hidden sm:inline'>{translate("Tasks")}</span>
            <TabBadge count={tabCounts.tasks} />
          </TabsTrigger>
          <TabsTrigger value='financial' className='gap-2'>
            <Banknote className='h-5 w-5' />
            <span className='hidden sm:inline'>{translate("Financial")}</span>
            <TabBadge count={tabCounts.financial} />
          </TabsTrigger>
          <TabsTrigger value='diaries' className='gap-2'>
            <BookOpen className='h-5 w-5' />
            <span className='hidden sm:inline'>{translate("Work Diaries")}</span>
            <TabBadge count={tabCounts.diaries} />
          </TabsTrigger>
          <TabsTrigger value='files' className='gap-2'>
            <FolderOpen className='h-5 w-5' />
            <span className='hidden sm:inline'>{translate("Files")}</span>
            <TabBadge count={tabCounts.files} />
          </TabsTrigger>
        </TabsList>

        {activeTab === "tasks" ? (
          <TasksAnalytics filters={filters} onCountLoaded={handleTasksCount} />
        ) : activeTab === "financial" ? (
          <FinancialAnalytics filters={filters} onCountLoaded={handleFinancialCount} />
        ) : activeTab === "diaries" ? (
          <WorkDiaryAnalytics filters={filters} onCountLoaded={handleDiariesCount} />
        ) : activeTab === "files" ? (
          <FileAnalytics filters={filters} onCountLoaded={handleFilesCount} />
        ) : null}
      </Tabs>
    </div>
  )
}
